import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Watermark from "../components/Watermark";
import Ticker, { QuoteStrip, ChromeDivider } from "../components/Ticker";
import TypingLine from "../components/TypingLine";
import ProductCard from "../components/ProductCard";
import HeroCarousel from "../components/HeroCarousel";
import { fetchFeatured } from "../lib/api";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    let alive = true;
    fetchFeatured().then((d) => { if (alive) setFeatured(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <>
      <section className="page-first-section relative flex items-start pb-4 sm:pb-6 md:pb-8 md:min-h-[calc(80vh_-_var(--nav-h))]" data-testid="hero-section">
        <Watermark word="FUTURE" />
        <div className="mt-container relative z-10 grid grid-cols-12 gap-x-6 gap-y-5 items-center py-2 sm:py-4">
          <div className="col-span-12 lg:col-span-8 mt-enter order-1 lg:row-start-1">
            <p className="font-mono text-[0.7rem] tracking-mt uppercase mb-4 sm:mb-6" style={{ color: "var(--mt-text-muted)" }}>
              <span className="mt-pulse-dot mt-keep-anim mr-3 align-middle" />
              <span className="mt-blood" style={{ fontWeight: 700 }}>NOW DROPPING</span>
              <span className="opacity-40 mx-2">·</span> Drop 01 Live
            </p>
            <h1 className="font-display font-extralight text-[clamp(2.8rem,8vw,7.5rem)]" style={{ lineHeight: 1.15, paddingBottom: "0.08em" }}>
              <span style={{ fontWeight: 200 }}>Wear&nbsp;the</span><br />
              <span
  className="mt-keep-anim italic"
  style={{
    fontWeight: 700,
    color: "#9f1116"
  }}
>
  Future.
</span>
            </h1>
          </div>

          {/* Product carousel — comes right after the heading on mobile/tablet so the product is visible without scrolling. Desktop: explicit row-start-1 + self-start keeps it parallel to the heading instead of sinking when row-span stretched it across two uneven rows. */}
          <div className="col-span-12 lg:col-span-4 mt-enter order-2 lg:order-3 lg:row-start-1 lg:self-start" style={{ transitionDelay: "180ms" }}>
            <div className="relative rounded-[22px] aspect-[4/5] sm:aspect-[3/4] lg:aspect-[3/4] mt-glass mt-rainbow-edge overflow-hidden mt-1 lg:mt-0">
              <HeroCarousel />
            </div>
          </div>

          <div className="col-span-12 lg:col-span-8 mt-enter order-3 lg:order-2 lg:row-start-2" style={{ transitionDelay: "80ms" }}>
            <p className="mt-italic-silver text-[clamp(1.05rem,1.6vw,1.35rem)]" style={{ maxWidth: 560 }}>
              A million stories. One thread. Built for those who move first and arrive ahead.
            </p>
            <div className="mt-7 sm:mt-10 flex flex-wrap gap-4">
              <Link to="/shop" className="mt-btn mt-rainbow-edge" data-testid="hero-cta-shop">
                Shop Now <ArrowRight size={14} />
              </Link>
              <Link to="/about" className="mt-btn" data-testid="hero-cta-story">Our Story</Link>
            </div>
            <div className="mt-7 sm:mt-10 hidden lg:block"><TypingLine /></div>
          </div>

          {/* Typing line on mobile/tablet: after everything else, so it never delays product visibility */}
          <div className="col-span-12 order-4 lg:hidden">
            <TypingLine />
          </div>
        </div>
      </section>

      <Ticker />

      <div className="my-16"><QuoteStrip quote="Every thread has a story" testId="quote-strip-1" /></div>

      <section className="mt-section relative" data-testid="featured-section">
        <Watermark word="LATEST" />
        <div className="mt-container relative z-10">
          <div className="flex items-end justify-between gap-6 mb-12 mt-enter">
            <div>
              <p className="font-mono text-[0.7rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>The Edit · 03 pieces</p>
              <h2 className="mt-3 font-display font-extralight text-[clamp(2.2rem,5.5vw,4.5rem)]" style={{ lineHeight: 1.15, paddingBottom: "0.06em" }}>
                Latest <span className="italic mt-chrome-text mt-keep-anim" style={{ fontWeight: 600 }}>Drop.</span>
              </h2>
            </div>
            <Link to="/shop" className="hidden sm:inline-flex mt-nav-link" data-testid="featured-view-all">View Shop &nbsp;→</Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {featured.map((p, i) => (
              <div key={p.id} className="mt-enter" style={{ transitionDelay: `${i * 90}ms` }}>
                <ProductCard product={p} index={i} />
              </div>
            ))}
          </div>

          {/* Mobile/tablet: "See All" is hidden above (next to the heading) below sm — give it a dedicated, full-width spot here instead */}
          <Link to="/shop" className="sm:hidden mt-btn w-full mt-8 flex items-center justify-center gap-2" data-testid="featured-view-all-mobile">
            See All <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <ChromeDivider />
    </>
  );
}
